import axios from 'axios';
import Config from '../config';
import { CITY_ZONE } from '../components/marketing/cityZones';

// Reporting client only. The shared axios instance already attaches the ERP session token.
// qtUrl is localhost:5003 in local dev and quotationerp only for a production build.
const base = `${Config.qtUrl}google-ads`;

const CITY_NAMES = Object.keys(CITY_ZONE).sort((left, right) => right.length - left.length);

const CITY_CODES = {
    ahmed: 'Ahmedabad',
    ahmedabad: 'Ahmedabad',
    blr: 'Bangalore',
    bangalore: 'Bangalore',
    bengaluru: 'Bangalore',
    bhubaneswar: 'Bhubaneshwar',
    bhubaneshwar: 'Bhubaneshwar',
    bbsr: 'Bhubaneshwar',
    chennai: 'Chennai',
    coimbatore: 'Coimbatore',
    hubli: 'Hubli',
    hubballi: 'Hubli',
    hubbali: 'Hubli',
    hyd: 'Hyderabad',
    hyderabad: 'Hyderabad',
    indore: 'Indore',
    jaipur: 'Jaipur',
    kochi: 'Kochi',
    cochin: 'Kochi',
    kol: 'Kolkata',
    kolkata: 'Kolkata',
    lucknow: 'Lucknow',
    mumbai: 'Mumbai',
    thane: 'Mumbai',
    borivali: 'Mumbai',
    navi: 'Mumbai',
    mysore: 'Mysore',
    mysuru: 'Mysore',
    ncr: 'NCR',
    delhi: 'NCR',
    noida: 'NCR',
    gurugram: 'NCR',
    gurgaon: 'NCR',
    ghaziabad: 'NCR',
    nagpur: 'Nagpur',
    patna: 'Patna',
    pune: 'Pune',
    raipur: 'Raipur',
    ranchi: 'Ranchi',
    surat: 'Surat',
    vijayawada: 'Vijayawada',
    vijaywada: 'Vijayawada',
    vizag: 'Visakhapatnam',
    visakhapatnam: 'Visakhapatnam',
};

function optionalNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function moneyNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
}

function citiesFromName(name) {
    const tokens = String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean);
    if (!tokens.length) return [];
    const firstTwo = tokens.slice(0, 2).join(' ');
    if (CITY_CODES[firstTwo]) return [CITY_CODES[firstTwo]];
    if (CITY_CODES[tokens[0]]) return [CITY_CODES[tokens[0]]];
    if (tokens[0].length >= 4) {
        const prefixHits = CITY_NAMES.filter((city) => city.toLowerCase().startsWith(tokens[0]));
        if (prefixHits.length === 1) return prefixHits;
    }
    const later = tokens.slice(1).find((token) => CITY_CODES[token]);
    return later ? [CITY_CODES[later]] : [];
}

function campaignCities(row) {
    if (row.sourceRow && Array.isArray(row.cities) && row.cities.length) {
        return [...new Set(row.cities.map((name) => String(name || '').trim()).filter(Boolean))];
    }
    if (row.citySource === 'manual' && Array.isArray(row.cities) && row.cities.length) {
        return [...new Set(row.cities.map((name) => String(name || '').trim()).filter(Boolean))];
    }
    const fromName = citiesFromName(row.campaignName || row.campaign_name || row.name);
    if (fromName.length) return fromName;
    const fromList = Array.isArray(row.cities) ? row.cities : [];
    if (fromList.length) return [...new Set(fromList.map((name) => String(name || '').trim()).filter(Boolean))];
    return row.city ? [String(row.city).trim()] : [];
}

function rawRows(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.records)) return payload.records;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.campaigns)) return payload.campaigns;
    return [];
}

function recordDate(row) {
    return String(row.date || row.day || row.segments?.date || '').slice(0, 10);
}

function normalizeRecord(row = {}) {
    const micros = row.costMicros ?? row.cost_micros;
    const spend = moneyNumber(row.spend ?? row.cost ?? (micros != null ? Number(micros) / 1000000 : 0));
    const clicks = moneyNumber(row.clicks);
    const impressions = moneyNumber(row.impressions);
    const campaignId = String(row.campaignId || row.campaign_id || row.id || '');
    const cities = campaignCities(row);
    const providedCpc = optionalNumber(row.costPerClick ?? row.cpc ?? row.averageCpc);

    return {
        campaignId,
        campaignName: row.campaignName || row.campaign_name || row.name || 'Untitled campaign',
        accountId: String(row.accountId || row.customerId || row.customer_id || ''),
        status: row.status || row.campaignStatus || row.campaign_status || '',
        spend,
        currency: row.currency || row.currencyCode || '',
        impressions,
        clicks,
        conversions: optionalNumber(row.conversions),
        costPerClick: providedCpc != null ? providedCpc : (clicks > 0 ? spend / clicks : null),
        cities,
        citySource: row.citySource || '',
        date: recordDate(row),
    };
}

function dedupeRecords(rows) {
    const byKey = new Map();
    rows.forEach((row) => {
        if (!row.campaignId) return;
        const key = row.date
            ? `${row.accountId}|${row.campaignId}|${row.date}`
            : `${row.accountId}|${row.campaignId}`;
        byKey.set(key, row);
    });
    return [...byKey.values()];
}

function aggregateCampaigns(records) {
    const byCampaign = new Map();
    records.forEach((row) => {
        const key = `${row.accountId}|${row.campaignId}`;
        const current = byCampaign.get(key) || {
            campaignId: row.campaignId,
            campaignName: row.campaignName,
            accountId: row.accountId,
            status: row.status,
            spend: 0,
            currency: row.currency || '',
            impressions: 0,
            clicks: 0,
            conversions: 0,
            hasConversions: false,
            cities: [],
        };
        current.campaignName = row.campaignName || current.campaignName;
        current.status = row.status || current.status;
        current.spend += row.spend;
        current.impressions += row.impressions;
        current.clicks += row.clicks;
        if (row.conversions != null) {
            current.conversions += row.conversions;
            current.hasConversions = true;
        }
        if (row.currency) current.currency = row.currency;
        current.cities = [...new Set([...current.cities, ...row.cities])];
        byCampaign.set(key, current);
    });

    return [...byCampaign.values()].map((row) => ({
        ...row,
        currency: row.currency || 'INR',
        conversions: row.hasConversions ? row.conversions : null,
        costPerClick: row.clicks > 0 ? row.spend / row.clicks : null,
        costPerConversion: row.hasConversions && row.conversions > 0 ? row.spend / row.conversions : null,
        leadCount: 0,
        erpConversions: 0,
        city: row.cities[0] || null,
    })).sort((left, right) => right.spend - left.spend);
}

function dailyFromRecords(records) {
    const byDate = new Map();
    records.forEach((row) => {
        if (!row.date) return;
        const current = byDate.get(row.date) || {
            date: row.date,
            spend: 0,
            clicks: 0,
            impressions: 0,
            currency: row.currency || null,
        };
        current.spend += row.spend;
        current.clicks += row.clicks;
        current.impressions += row.impressions;
        if (row.currency) current.currency = row.currency;
        byDate.set(row.date, current);
    });
    return [...byDate.values()].sort((left, right) => left.date.localeCompare(right.date));
}

function cityTagMap(cityTags) {
    const tags = new Map();
    (Array.isArray(cityTags) ? cityTags : []).forEach((row) => {
        const campaignId = String(row?.campaignId || '');
        const city = String(row?.city || '').trim();
        if (campaignId && city) tags.set(campaignId, city);
    });
    return tags;
}

function withManualCity(row, tags) {
    const city = tags.get(String(row.campaignId || ''));
    if (!city) return row;
    return { ...row, cities: [city], city, citySource: 'manual' };
}

export function buildGoogleAdsReport(payload) {
    const tags = cityTagMap(payload?.cityTags);
    const records = dedupeRecords(rawRows(payload).map(normalizeRecord)).map((row) => withManualCity(row, tags));
    const campaigns = applyCampaignLeads(aggregateCampaigns(records), payload?.campaignLeads)
        .map((row) => withManualCity(row, tags));
    const dated = records.some((row) => row.date);
    const daily = dated
        ? dailyFromRecords(records)
        : dailyFromRecords(dedupeRecords((Array.isArray(payload?.daily) ? payload.daily : []).map(normalizeRecord)).map((row) => withManualCity(row, tags)));

    return { campaigns, daily, records };
}

function applyCampaignLeads(campaigns, campaignLeads) {
    const byId = new Map(campaigns.map((campaign) => [String(campaign.campaignId), campaign]));
    (Array.isArray(campaignLeads) ? campaignLeads : []).forEach((row) => {
        const campaignId = String(row.campaignId || '');
        if (!campaignId) return;
        const existing = byId.get(campaignId);
        const sqlReal = Number(row.sqlReal) || 0;
        const salesTbc = Number(row.salesTbc) || 0;
        if (existing) {
            existing.leadCount = Number(row.leadCount) || 0;
            existing.sqlReal = sqlReal;
            existing.salesTbc = salesTbc;
            existing.totalReal = sqlReal + salesTbc;
            existing.salesWon = Number(row.salesWon) || 0;
            existing.salesWonAmount = Number(row.salesWonAmount) || 0;
            existing.days = Array.isArray(row.days) ? row.days : [];
            if (!existing.cities.length) {
                existing.cities = citiesFromName(existing.campaignName || row.campaignName);
                existing.city = existing.cities[0] || null;
            }
            return;
        }
        if (!row.sourceRow && !campaignId.startsWith('source|')) return;
        const city = row.city || 'Untagged';
        byId.set(campaignId, {
            campaignId,
            campaignName: row.campaignName || campaignId,
            accountId: '',
            status: '',
            spend: 0,
            currency: 'INR',
            impressions: 0,
            clicks: 0,
            conversions: null,
            cities: [city],
            city,
            leadCount: Number(row.leadCount) || 0,
            sqlReal,
            salesTbc,
            totalReal: sqlReal + salesTbc,
            salesWon: Number(row.salesWon) || 0,
            salesWonAmount: Number(row.salesWonAmount) || 0,
            days: Array.isArray(row.days) ? row.days : [],
            sourceRow: true,
        });
    });
    return [...byId.values()].sort((left, right) => (
        (right.spend - left.spend) || (right.leadCount - left.leadCount)
    ));
}

export function dailySeries(records, predicate) {
    const rows = predicate ? records.filter(predicate) : records;
    return dailyFromRecords(rows);
}

export async function getGoogleAdsReport({ startDate, endDate, signal }) {
    const { data } = await axios.get(`${base}/campaigns`, {
        params: { startDate, endDate },
        signal,
    });
    const report = buildGoogleAdsReport(data);
    if (report.daily.length || signal?.aborted) return report;

    try {
        const dailyResponse = await axios.get(`${base}/daily`, {
            params: { startDate, endDate },
            signal,
        });
        const payload = dailyResponse.data;
        const dailyReport = buildGoogleAdsReport(
            Array.isArray(payload) || payload?.campaigns || payload?.records || payload?.rows
                ? payload
                : { daily: payload?.daily || [] }
        );
        return {
            campaigns: report.campaigns,
            daily: dailyReport.daily,
            records: report.records.length ? report.records : dailyReport.records,
        };
    } catch (error) {
        if (isCanceled(error)) throw error;
        return report;
    }
}

export async function syncGoogleAdsReport({ startDate, endDate, incremental = true, full = false }) {
    const { data } = await axios.post(`${base}/sync`, {
        startDate,
        endDate,
        from: startDate,
        to: endDate,
        incremental,
        full,
    });
    return data;
}

export async function getGoogleAdsLeadDetails({ startDate, endDate, bucket, campaignId, campaignIds, signal }) {
    const { data } = await axios.get(`${base}/leads`, {
        params: { startDate, endDate, bucket, campaignId, campaignIds },
        signal,
    });
    return data;
}

export async function saveGoogleAdsCityTag({ campaignId, city }) {
    const { data } = await axios.put(`${base}/city-tags`, { campaignId, city });
    return data;
}

export async function getGoogleAdsLeadTargets({ signal } = {}) {
    const { data } = await axios.get(`${base}/targets`, { signal });
    const rows = Array.isArray(data?.targets) ? data.targets : [];
    return rows.reduce((map, row) => {
        const city = String(row.city || '').trim();
        const leadTarget = Number(row.leadTarget);
        if (city && Number.isInteger(leadTarget)) map[city] = leadTarget;
        return map;
    }, {});
}

export async function saveGoogleAdsLeadTarget({ city, leadTarget }) {
    const { data } = await axios.put(`${base}/targets`, { city, leadTarget });
    return data;
}

export function adsErrorMessage(error, fallback) {
    return error?.response?.data?.message
        || error?.response?.data?.error
        || fallback;
}

export function isCanceled(error) {
    return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
}
